import { useTranslation } from 'react-i18next';
import type { Epic } from '../../../../../api/tasks-client/task-client.type';
import { useDeepCompareEffect } from '../../../../../hooks/useDeepCompareEffect';
import { useStrategyStore, useThroughputTabStore } from '../../../../../store/strategy-store/strategy-store';
import { setThroughputSelectedEpics } from '../../../../../store/strategy-store/strategy-store.actions';
import { inkLight } from '../../../../../styles/design-tokens';
import { Text } from '../../../../../ui-library/typography/typography';
import { FiltersListEpics } from '../../components/filters-list/filters-list-epics';
import { MAX_SELECTION } from '../../initiative-performance.constants';
import { useContributingEpics } from '../../initiative-performance.hooks';

/**
 * Throughput tab's contributing-epics sidebar.
 *
 * Renders the per-epic subtext by summing the same `contributing_epics_over_time`
 * time-series the chart uses, so the count next to each epic equals the sum of
 * that epic's chart bars under the active Tasks/Points metric.
 *
 * @returns {JSX.Element} The contributing-epics filter list for the Throughput tab
 */
export const FiltersEpics = () => {
  const i18n = useTranslation();
  const contributingEpics = useContributingEpics();
  const selectedEpics = useThroughputTabStore((state) => state.selectedEpics);
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const metric = useThroughputTabStore((state) => state.metric);

  useDeepCompareEffect(() => {
    if (contributingEpics.length > 0) {
      setThroughputSelectedEpics(contributingEpics.slice(0, MAX_SELECTION));
    }
  }, [contributingEpics]);

  const getItemExtraInfo = (epic: Epic) => {
    const series = initiative?.contributing_epics_over_time?.[epic.id]?.[metric] ?? {};
    const total = Object.values(series).reduce<number>((sum, value) => sum + (value ?? 0), 0);
    return (
      <Text size="small" color={inkLight}>
        {`${total} ${(total === 1 ? i18n.t('common.work_unit') : i18n.t('common.work_units')).toLowerCase()}`}
      </Text>
    );
  };

  return (
    <FiltersListEpics
      epics={contributingEpics}
      selectedEpics={selectedEpics}
      onSelectEpics={setThroughputSelectedEpics}
      getItemExtraInfo={getItemExtraInfo}
    />
  );
};
